import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <div className="flex gap-1" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i <= n ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#D4AF37]/30"}`}
        aria-hidden="true"
      />
    ))}
  </div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [text, setText] = useState("");

  useEffect(() => {
    axios.get(`${API}/reviews/approved`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { name, rating, text });
      toast.success("Thank you! Your review will appear once approved.");
      setOpen(false);
      setName("");
      setText("");
      setRating(5);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const placeholders = [0, 1, 2];

  return (
    <section id="reviews" className="bg-white py-20 sm:py-24" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          align="center"
          eyebrow="Kind Words"
          title="What Our Customers Say"
          subtitle="We are a growing family business — real reviews from our Camden & South Jersey neighbors will live here."
        />

        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {reviews.length > 0
            ? reviews.map((r, i) => (
                <Reveal key={r.id} delay={(i % 3) * 0.1}>
                  <figure className="h-full rounded-2xl border border-[#8E7CC3]/15 bg-[#FAF8FF] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`review-card-${i}`}>
                    <Quote className="h-6 w-6 text-[#D4AF37]" aria-hidden="true" />
                    <blockquote className="mt-4 text-sm leading-relaxed text-[#2C2A35] sm:text-base">{r.text}</blockquote>
                    <figcaption className="mt-6 flex items-center justify-between">
                      <span className="text-sm font-bold text-[#674EA7]">{r.name}</span>
                      <Stars n={r.rating} />
                    </figcaption>
                  </figure>
                </Reveal>
              ))
            : placeholders.map((i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <figure
                    className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#8E7CC3]/30 bg-[#FAF8FF] p-10 text-center"
                    data-testid={`review-placeholder-${i}`}
                  >
                    <Stars n={5} />
                    <blockquote className="font-script mt-6 text-2xl text-[#8E7CC3]">
                      “Your review could be here.”
                    </blockquote>
                    <figcaption className="mt-4 text-xs font-semibold uppercase tracking-widest text-[#6B6776]">
                      Future happy customer
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
        </div>

        <div className="mt-12 text-center">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="leave-review-button"
                className="rounded-full border-2 border-[#D4AF37]/60 bg-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#2C2A35] transition-transform hover:scale-[1.02]"
              >
                Leave a Review
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl sm:max-w-md" data-testid="review-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Share Your Experience</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="mt-4 space-y-5" data-testid="review-form">
                <div>
                  <Label htmlFor="r-name">Your Name *</Label>
                  <Input id="r-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-xl" placeholder="Jane S." data-testid="review-name-input" />
                </div>
                <div>
                  <Label>Your Rating *</Label>
                  <div className="mt-2 flex gap-2" data-testid="review-rating-input">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setRating(i)}
                        aria-label={`${i} star${i > 1 ? "s" : ""}`}
                        data-testid={`review-star-${i}`}
                        className="transition-transform hover:scale-110"
                      >
                        <Star className={`h-7 w-7 ${i <= rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#D4AF37]/30"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="r-text">Your Review *</Label>
                  <Textarea id="r-text" required rows={4} value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5 rounded-xl" placeholder="Tell us how we did..." data-testid="review-text-input" />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="review-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#8E7CC3] px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-transform hover:scale-[1.01] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  Submit Review
                </button>
                <p className="text-center text-xs text-[#6B6776]">Reviews appear on the site after a quick approval.</p>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
